import React, { HTMLProps } from "react";
import { Row, Col } from "../Layout";
import { Button } from "../button";
import { Pojo } from "types/Galaxy";
import { useDrag, useDrop } from "react-dnd";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import PojoFilterableList from "../FilterableList/PojoFilterableList";
import { Fa } from "composants/Icon";
import { tw } from "twind";

const NODE_TYPE = "pickList&D";
export const AVAILABLE = "AVAILABLE";
export const CHOSEN = "CHOSEN";
export type LaneName = "AVAILABLE" | "CHOSEN";

type CardWrapperAllProps = {
  id: string;
  index: number;
  laneId: LaneName;
  moveCard?(draggedId: string, overId: string, laneId: LaneName): void;
  changeLane?(id: string, laneId: string): void;
  dropOut?(): void;
} & HTMLProps<HTMLDivElement>;

function CardWrapper({
  id,
  index,
  laneId,
  moveCard,
  changeLane,
  dropOut,
  children,
  ...otherProps
}: CardWrapperAllProps) {
  const [{ isDragging }, dragRef] = useDrag({
    type: NODE_TYPE,
    item: { index, id, laneId },
    end(item, monitor) {
      const dropResult = monitor.getDropResult() as { laneId: LaneName } | null;
      if (changeLane && dropResult && dropResult.laneId !== item.laneId) {
        changeLane(item.id, dropResult.laneId);
      } else if (dropOut) {
        dropOut();
      }
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [, dropRef] = useDrop({
    accept: NODE_TYPE,
    hover(item: { id: string; laneId: LaneName }) {
      if (item.id !== id && moveCard) {
        moveCard(item.id, id, laneId);
      }
    },
  });

  return (
    <div
      ref={(node) => {
        dragRef(node);
        dropRef(node);
      }}
      {...otherProps}
      style={{ ...otherProps.style, opacity: isDragging ? 0 : 1 }}
    >
      {children}
    </div>
  );
}

export function Lane({
  id,
  children,
  ...restProps
}: React.PropsWithChildren<{ id: LaneName } & HTMLProps<HTMLDivElement>>) {
  const [, dropRef] = useDrop({
    accept: NODE_TYPE,
    drop: () => ({ laneId: id }),
  });

  return (
    <div ref={dropRef} {...restProps}>
      {children}
    </div>
  );
}

// Gère le fait de cacher les composants choisis dans la liste des composants disponibles
export function getAvailableNotChosen(available: Pojo[], selected: Pojo[]) {
  return available.filter((current) => selected.findIndex((s) => s.id === current.id) === -1);
}

interface CustomablePicklistProps {
  // liste des élément disponible
  available: Pojo[];
  // Liste des field sur lequel la picklist peut faire une recherche parmi les éléments dispo
  availableSearchFields?: string[];
  // Liste des éléments choisis
  chosen: Pojo[];
  // Liste des field sur lequel la picklist peut faire une recherche parmi les éléments choisi
  chosenSearchField?: string[];
  invertPicklist?: boolean;
  // Fonction appeler dès qu'une carte est déplacée
  moveCard(draggedId: string, overId: string, laneId: LaneName): void;
  // Fonction appeler lors d'un drop sur une lane différente
  changeLane?(id: string, laneId: string): void;
  // Fonctions permettant de définir le rendu de chaque card
  renderChosenCompo(pojo: Pojo): JSX.Element;
  renderAvailableCompo(pojo: Pojo): JSX.Element;
  // Fonction définissant le comportement à adopté lorsqu'une card est cliquée
  // (par défaut ça devrait selectionné l'élément cliquer)
  addSelectedPojo(laneId: string, pojoId: string, replace: boolean): void;
  chooseSelected?(): void;
  unChooseSelected?(): void;
  chooseAll?(): void;
  unChooseAll?(): void;
  putOnTop?(): void;
  putToBottom?(): void;
  up?(): void;
  down?(): void;
  dropOut?(): void;
}

function CustomablePicklist(props: CustomablePicklistProps) {
  function buildChildrenList(laneId: LaneName): (pojos: Pojo[]) => JSX.Element {
    return (pojos: Pojo[]) => (
      <>
        {pojos.map((pojo, index) =>
          pojo.__fake ? (
            // Le fake est un simple div : pas de CardWrapper (drop target),
            // le drop passe directement à la Lane parente → getDropResult() fiable.
            <div key={pojo.id}>
              {laneId === AVAILABLE && props.renderAvailableCompo(pojo)}
              {laneId === CHOSEN && props.renderChosenCompo(pojo)}
            </div>
          ) : (
            <div
              key={pojo.id}
              onClick={(e) => {
                props.addSelectedPojo(laneId, pojo.id, !e.ctrlKey);
              }}
            >
              <CardWrapper
                id={pojo.id}
                laneId={laneId}
                index={index}
                moveCard={props.moveCard}
                changeLane={props.changeLane}
                dropOut={props.dropOut}
              >
                {laneId === AVAILABLE && props.renderAvailableCompo(pojo)}
                {laneId === CHOSEN && props.renderChosenCompo(pojo)}
              </CardWrapper>
            </div>
          ),
        )}
      </>
    );
  }

  type ButtonDef = { icon: IconProp; handler: (() => void) | undefined };

  const transferButtons: ButtonDef[] = props.invertPicklist
    ? [
        { icon: "angle-right", handler: props.unChooseSelected },
        { icon: "angle-left", handler: props.chooseSelected },
        { icon: "angle-double-right", handler: props.unChooseAll },
        { icon: "angle-double-left", handler: props.chooseAll },
      ]
    : [
        { icon: "angle-right", handler: props.chooseSelected },
        { icon: "angle-left", handler: props.unChooseSelected },
        { icon: "angle-double-right", handler: props.chooseAll },
        { icon: "angle-double-left", handler: props.unChooseAll },
      ];

  const orderButtons: ButtonDef[] = [
    { icon: "angle-double-up", handler: props.putOnTop },
    { icon: "angle-up", handler: props.up },
    { icon: "angle-down", handler: props.down },
    { icon: "angle-double-down", handler: props.putToBottom },
  ];

  const allButtons = [...transferButtons, ...orderButtons];

  const moveButton = (
    <Col className={tw`flex items-center justify-center overflow-hidden`}>
      <div
        className={tw`flex flex-col items-center justify-center gap-1 overflow-y-auto overflow-x-hidden`}
      >
        {allButtons.map(({ icon, handler }, i) =>
          handler ? (
            <Button key={i} className="is-info" onClick={handler}>
              <span className="icon is-small">
                <Fa icon={icon} />
              </span>
            </Button>
          ) : null,
        )}
      </div>
    </Col>
  );

  return (
    <Row>
      <Col span={5}>
        {!props.invertPicklist ? (
          <Lane id={AVAILABLE}>
            <PojoFilterableList
              pojos={getAvailableNotChosen(props.available, props.chosen)}
              fields={props.availableSearchFields}
              buildChildrenList={buildChildrenList(AVAILABLE)}
            />
          </Lane>
        ) : (
          <Lane id={CHOSEN}>
            <PojoFilterableList
              pojos={props.chosen}
              fields={props.chosenSearchField}
              buildChildrenList={buildChildrenList(CHOSEN)}
            />
          </Lane>
        )}
      </Col>
      {moveButton}
      <Col span={5}>
        {!props.invertPicklist ? (
          <Lane id={CHOSEN}>
            <PojoFilterableList
              pojos={props.chosen}
              fields={props.chosenSearchField}
              buildChildrenList={buildChildrenList(CHOSEN)}
            />
          </Lane>
        ) : (
          <Lane id={AVAILABLE}>
            <PojoFilterableList
              pojos={getAvailableNotChosen(props.available, props.chosen)}
              fields={props.availableSearchFields}
              buildChildrenList={buildChildrenList(AVAILABLE)}
            />
          </Lane>
        )}
      </Col>
    </Row>
  );
}

export default CustomablePicklist;
